import { useRef, useState } from "react";
import { Download, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Badge, Button, ErrorNote, Modal, Note } from "@/components/portal/Ui";

const STATE_TONE = { ready: "green", duplicate: "orange", error: "red" };
const STATE_LABEL = { ready: "Ready", duplicate: "Duplicate — skipped", error: "Error" };

export function CampaignImportModal({ open, onClose, onImported }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setPreview(null);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/campaigns/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "anytribe-campaign-import-template.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const choose = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError("");
    setPreview(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/campaigns/import/preview", form);
      setPreview({ ...data, filename: file.name });
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    const rows = (preview?.rows || []).filter((r) => r.state === "ready");
    if (rows.length === 0) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/campaigns/import/commit", { rows });
      toast.success(`${data.created.length} campaign${data.created.length === 1 ? "" : "s"} imported`);
      reset();
      await onImported();
      onClose();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const ready = preview?.summary?.ready || 0;

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Import campaigns from CSV"
      wide
      testId="campaign-import-modal"
    >
      <div className="space-y-4">
        <ErrorNote message={error} />
        <Note tone="neutral">
          Nothing is saved until you press Import. Existing campaigns are never overwritten — any row
          matching a campaign name that already exists for that client is skipped. Clients must exist
          first; create them in Users & Clients.
        </Note>

        <div className="flex flex-wrap items-center gap-2.5">
          <Button variant="outline" onClick={downloadTemplate} testId="import-template-button">
            <Download className="h-4 w-4" />
            Download template
          </Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()} busy={busy} testId="import-choose-button">
            <Upload className="h-4 w-4" />
            {preview ? "Choose another file" : "Choose CSV file"}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            onChange={choose}
            className="hidden"
            data-testid="import-file-input"
          />
          {preview && <span className="text-xs body-text">{preview.filename}</span>}
        </div>

        {preview && (
          <>
            <div className="flex flex-wrap gap-2" data-testid="import-summary">
              <Badge tone="green" testId="import-count-ready">{preview.summary.ready} ready</Badge>
              <Badge tone="orange" testId="import-count-duplicate">{preview.summary.duplicate} duplicate</Badge>
              <Badge tone="red" testId="import-count-error">{preview.summary.error} error</Badge>
              <Badge tone="neutral">{preview.summary.total} rows in file</Badge>
            </div>

            <div className="scroll-x max-h-[380px] overflow-y-auto rounded-xl border border-[#EFEAE1]">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="sticky top-0 bg-[#FBF9F5]">
                  <tr className="text-left text-xs font-bold uppercase tracking-[0.08em] text-[#8a8378]">
                    <th className="p-3">Line</th>
                    <th className="p-3">Campaign</th>
                    <th className="p-3">Client</th>
                    <th className="p-3">Dates</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Posts / KOL</th>
                    <th className="p-3">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row) => (
                    <tr
                      key={row.line}
                      className="border-t border-[#F5F1EA] align-top"
                      data-testid={`import-row-${row.line}`}
                    >
                      <td className="p-3 tabular body-text">{row.line}</td>
                      <td className="p-3 font-semibold">{row.campaign_name || "—"}</td>
                      <td className="p-3">{row.client || "—"}</td>
                      <td className="p-3 body-text">
                        {row.start_date || "—"} → {row.end_date || "—"}
                      </td>
                      <td className="p-3">{row.status}</td>
                      <td className="p-3 tabular">
                        {row.posts_per_kol == null ? "—" : row.posts_per_kol}
                      </td>
                      <td className="p-3">
                        <Badge tone={STATE_TONE[row.state]} testId={`import-state-${row.line}`}>
                          {STATE_LABEL[row.state]}
                        </Badge>
                        {row.messages.length > 0 && (
                          <ul className="mt-1.5 space-y-0.5 text-xs body-text">
                            {row.messages.map((message) => (
                              <li key={message}>{message}</li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2.5">
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
            testId="import-cancel-button"
          >
            Cancel
          </Button>
          <Button busy={busy} disabled={ready === 0} onClick={commit} testId="import-commit-button">
            Import {ready} campaign{ready === 1 ? "" : "s"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
